export class ImageConverter {

    static async urlToImg(url: string): Promise<HTMLImageElement> {
        return new Promise(cb => {
            let img = new Image();
            img.onload = () => cb(img)
            img.setAttribute('src', url);
            img.style.display = 'none';
            document.body.appendChild(img)
        })
    }

    static async urlToUri(url: string): Promise<string> {
        return new Promise(async cb => {
            const img = await ImageConverter.urlToImg(url);
            const blob = await ImageConverter.imgToBlob(img);
            const localUrl = URL.createObjectURL(blob);
            cb(localUrl);
        })
    }

    static async urlToBlob(url: string): Promise<Blob> {
        return new Promise(async cb => {
            const img = await ImageConverter.urlToImg(url);
            const blob = await ImageConverter.imgToBlob(img);
            cb(blob)
        })
    }

    static async urlToFile(url: string): Promise<File> {
        return new Promise(async cb => {
            const blob = await ImageConverter.urlToBlob(url);
            const file = ImageConverter.blobToFile(blob);
            cb(file)
        })
    }

    static async imgToUri(img: HTMLImageElement): Promise<string> {
        return new Promise(async cb => {
            const blob = await ImageConverter.imgToBlob(img);
            const localUrl = URL.createObjectURL(blob)
            cb(localUrl);
        })
    }

    static async imgToBlob(img: HTMLImageElement): Promise<Blob> {
        return new Promise(cb => {
            let canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            let ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(res => cb(res));
        });
    }

    static async imgToFile(img: HTMLImageElement): Promise<File> {
        return new Promise(async cb => {
            const blob = await ImageConverter.imgToBlob(img);
            const file = ImageConverter.blobToFile(blob)
            cb(file)
        })
    }

    static async blobToUri(blob: Blob): Promise<string> {
        return new Promise(cb => {
            const fr = new FileReader();
            fr.onload = () => cb(fr.result as string);
            fr.readAsDataURL(blob);
        })
    }

    static async blobToImg(blob: Blob): Promise<HTMLImageElement> {
        return new Promise(async cb => {
            const localUrl = await ImageConverter.blobToUri(blob);
            const img = await ImageConverter.urlToImg(localUrl);
            cb(img)
        })
    }

    static blobToFile(blob: Blob): File {
        return new File([blob], 'img.jpeg');
    }

    static async fileToUri(file: File): Promise<string> {
        return new Promise(async cb => {
            const blob = await ImageConverter.fileToBlob(file);
            const localUrl = await ImageConverter.blobToUri(blob);
            cb(localUrl);
        })
    }

    static async fileToImg(file: File): Promise<HTMLImageElement> {
        return new Promise(async cb => {
            const blob = await ImageConverter.fileToBlob(file);
            const img = await ImageConverter.blobToImg(blob);
            cb(img);
        })
    }

    static async fileToBlob(file: File): Promise<Blob> {
        return new Promise(cb => {
            const fr = new FileReader();
            fr.onload = () => cb(new Blob([new Uint8Array(fr.result as ArrayBuffer)], { type: file.type }));
            fr.readAsArrayBuffer(file);
        })
    }

    static async avoidCORS(url: string): Promise<string> {
        return new Promise(cb => {
            let obj = document.createElement('object');
            obj.type = 'image/jpeg';
            obj.data = url;
            const blob = new Blob([obj.outerHTML], { type: 'image/jpeg' });
            const localUrl = ImageConverter.blobToUri(blob);
            cb(localUrl);
        })
    }

    static base64ToBlob(base64: string, format = "image/png"): Blob {
        const [metadata, base64Data] = base64.split(',');
    
        const imageFormat = metadata.match(/data:(.*?);base64/)?.[1] || format;
        const byteCharacters = atob(base64Data ?? metadata);
        const byteArrays = [];
    
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
    
        // Create and return a Blob object from the byte arrays
        return new Blob(byteArrays, { type: imageFormat });
    }

    static canvasToBlob(canvas: HTMLCanvasElement, format = "image/png"): Blob {
        const base64 = canvas.toDataURL(format);
        const blob = ImageConverter.base64ToBlob(base64.split(',').pop(), format);
        return blob;
    }

    static async convertImageToBase64(url: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.onload = () => {
                const reader = new FileReader();
                reader.onloadend = () => {
                resolve(reader.result as string);
                };
                reader.onerror = reject;
                reader.readAsDataURL(xhr.response);
            };
            xhr.onerror = reject;
            xhr.open('GET', url);
            xhr.responseType = 'blob';
            xhr.send();
        });
      }
      
    static async blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
            resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    static domToSvg(element: HTMLElement): string {
        const svgElement = element.cloneNode(true) as HTMLElement;
        
        // Customize this part if needed, such as adding SVG-specific attributes
        svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        
        // Wrap the element in an SVG tag
        const svgWrapper = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgWrapper.appendChild(svgElement);
        
        // Set the viewBox or other necessary attributes for the SVG
        svgWrapper.setAttribute('viewBox', `0 0 ${element.clientWidth} ${element.clientHeight}`);
        
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svgWrapper);
        
        return svgString;
    }
      
    static async base64SvgToBase64Png(svgString: string, width: number, height: number): Promise<string> {
        return new Promise((resolve, reject) => {
            // Create an Image element
            const img = new Image();
            
            img.onload = () => {
                // Create a canvas element
                const canvas = document.createElement('canvas');
                canvas.width = width || img.width;
                canvas.height = height || img.height;
                const context = canvas.getContext('2d');
          
                // Draw the image on the canvas
                context.drawImage(img, 0, 0, canvas.width, canvas.height);
          
                // Convert the canvas content to a Base64-encoded PNG
                const base64Png = canvas.toDataURL('image/png');
          
                // Resolve the promise with the Base64 PNG string
                resolve(base64Png);
              };
          
              // Handle image loading errors
              img.onerror = (error) => {
                reject(error);
              };

              img.src = svgString;
          });
    }
}
